/**
 * Endings — data definitions for all 14 PM fate endings (MY-1979).
 *
 * No Phaser import. Pure data consumed by PMFateScene and endingGating.
 */

export type EndingLockKind = 'open' | 'hero-ghost' | 'refused-all-deals' | 'hard-all-evidence';

export interface EndingDef {
  /** Stable id — used as cutscene key prefix 'ending-{id}' and save key. */
  id: string;
  /** Short display title. */
  title: string;
  /** One-line flavour description shown under the title when selected. */
  description: string;
  /** Tone tag (display only). */
  tone: string;
  /** Gate condition. 'open' = always selectable once PM is KO'd. */
  lockKind: EndingLockKind;
  /** Hint text shown on locked entries explaining how to unlock. */
  lockHint: string;
}

export const ENDINGS: readonly EndingDef[] = [
  // ── Open endings (selectable immediately) ──────────────────────────────────
  {
    id: 'peoples-verdict',
    title: "The People's Verdict",
    description: 'Hand him to the crowd and the courts.',
    tone: 'Lawful',
    lockKind: 'open',
    lockHint: '',
  },
  {
    id: 'whole-truth',
    title: 'The Whole Truth',
    description: 'Leak the MANOR files.',
    tone: 'Heroic',
    lockKind: 'open',
    lockHint: '',
  },
  {
    id: 'bigger-than-him',
    title: 'Bigger Than Him',
    description: "Walk away. You're not them.",
    tone: 'Mercy',
    lockKind: 'open',
    lockHint: '',
  },
  {
    id: 'meet-the-new-boss',
    title: 'Meet the New Boss',
    description: 'Take his bribe. Become MANOR.',
    tone: 'Dark',
    lockKind: 'open',
    lockHint: '',
  },
  {
    id: 'cancelled',
    title: 'Cancelled',
    description: 'Livestream his grovelling. Meme him into oblivion.',
    tone: 'Comedic',
    lockKind: 'open',
    lockHint: '',
  },
  {
    id: 'strings-attached',
    title: 'Strings Attached',
    description: 'Keep him in office as your puppet.',
    tone: 'Grey',
    lockKind: 'open',
    lockHint: '',
  },
  {
    id: 'live-at-ten',
    title: 'Live at Ten',
    description: 'Force a televised confession and resignation.',
    tone: 'Satirical',
    lockKind: 'open',
    lockHint: '',
  },
  {
    id: 'out-the-front-door',
    title: 'Out the Front Door',
    description: 'Open the lobby doors. Let the mob decide.',
    tone: 'Dark/Ambiguous',
    lockKind: 'open',
    lockHint: '',
  },
  {
    id: 'salt-the-earth',
    title: 'Salt the Earth',
    description: 'Ignore him. Torch the MANOR archive.',
    tone: 'Nihilist',
    lockKind: 'open',
    lockHint: '',
  },
  {
    id: 'one-last-use',
    title: 'One Last Use',
    description: 'Force him to repeal his own laws.',
    tone: 'Pragmatic',
    lockKind: 'open',
    lockHint: '',
  },
  {
    id: 'the-empty-chair',
    title: 'The Empty Chair',
    description: 'Sit on his throne. Photograph it. Leave.',
    tone: 'Symbolic',
    lockKind: 'open',
    lockHint: '',
  },
  // ── Gated endings (drive replay) ──────────────────────────────────────────
  {
    id: 'ghost-protocol',
    title: 'Ghost Protocol',
    description: 'Vanish with the evidence. The work continues underground.',
    tone: 'Cold',
    lockKind: 'hero-ghost',
    lockHint: 'Clear the campaign as GHOST',
  },
  {
    id: 'full-circle',
    title: 'Full Circle',
    description: 'The clean ending. The city actually changes.',
    tone: 'True',
    lockKind: 'refused-all-deals',
    lockHint: 'Refuse every mid-game deal-branch bribe',
  },
  {
    id: 'follow-the-money',
    title: 'Follow the Money',
    description: 'Astor-Pemberton reveals the PM was always a figurehead.',
    tone: 'Secret/Sequel',
    lockKind: 'hard-all-evidence',
    lockHint: 'Hard difficulty + all evidence collectibles',
  },
];

export const ENDINGS_BY_ID: Readonly<Record<string, EndingDef>> = Object.fromEntries(
  ENDINGS.map((e) => [e.id, e]),
);

export function getEndingDef(id: string): EndingDef | undefined {
  return ENDINGS_BY_ID[id];
}
